import { Modal } from '@odin-labs/components';
import {
  ConnectorStatus,
  useGetConnectorCatalogQuery,
} from 'apollo/generated/client-operations';
import {
  CircleCheckIcon,
  ClockIcon,
  ExclamationCircleIcon,
} from 'components/icons';
import { LoadingError } from 'components/loadingError';
import type { ConnectorCatalogItem } from 'containers/jobsiteConfiguration/tabs/connectors/connectorTypes';
import React from 'react';
import { connectorCatalog } from './connectorCatalog';

type ConnectorCatalogDrawerProps = {
  isOpen: boolean;
  siteId: string;
  onSelect: (item: ConnectorCatalogItem) => void;
  onClose: () => void;
};

type ConnectorInstanceStatus = {
  label: string;
  className: string;
  Icon: typeof CircleCheckIcon;
};

const getInstanceStatus = (
  status: ConnectorStatus,
): ConnectorInstanceStatus => {
  switch (status) {
    case ConnectorStatus.Draft:
      return {
        label: 'Not connected',
        className: 'odin-text-gray-600',
        Icon: ExclamationCircleIcon,
      };
    case ConnectorStatus.Connecting:
      return {
        label: 'Connecting',
        className: 'odin-text-yellow-700',
        Icon: ClockIcon,
      };
    case ConnectorStatus.ConnectionFailed:
      return {
        label: 'Connection failed',
        className: 'odin-text-red-700',
        Icon: ExclamationCircleIcon,
      };
    case ConnectorStatus.Connected:
      return {
        label: 'Connected',
        className: 'odin-text-yellow-700',
        Icon: ClockIcon,
      };
    case ConnectorStatus.Enabling:
      return {
        label: 'Enabling',
        className: 'odin-text-yellow-700',
        Icon: ClockIcon,
      };
    case ConnectorStatus.EnablementFailed:
      return {
        label: 'Enablement failed',
        className: 'odin-text-red-700',
        Icon: ExclamationCircleIcon,
      };
    case ConnectorStatus.Enabled:
      return {
        label: 'Connected',
        className: 'odin-text-green-700',
        Icon: CircleCheckIcon,
      };
    case ConnectorStatus.Disabled:
      return {
        label: 'Disabled',
        className: 'odin-text-gray-600',
        Icon: ExclamationCircleIcon,
      };
    default: {
      const unsupportedStatus: never = status;
      throw new Error(`Unsupported Connector status: ${unsupportedStatus}`);
    }
  }
};

export function ConnectorCatalogDrawer({
  isOpen,
  siteId,
  onSelect,
  onClose,
}: ConnectorCatalogDrawerProps): React.ReactElement {
  const { data, loading, error } = useGetConnectorCatalogQuery({
    variables: { siteId },
    fetchPolicy: 'no-cache',
    skip: !isOpen,
  });
  const items = data?.connectorCatalog.items ?? [];

  return (
    <Modal
      open={isOpen}
      setOpen={(open) => !open && onClose()}
      variant="drawer"
      size="sm"
      title="Add a connector"
      actionsPanel={null}
      onCancel={onClose}
      cancelText="Close"
    >
      <LoadingError loading={loading} error={error} />
      {!loading && !error && (
        <div className="odin-divide-y odin-divide-gray-200 odin-overflow-hidden odin-rounded-md odin-border odin-border-gray-200">
          {items.map((item) => {
            const instanceStatus = item.instance
              ? getInstanceStatus(item.instance.status)
              : null;
            const InstanceStatusIcon = instanceStatus?.Icon;
            return (
              <button
                key={item.type}
                type="button"
                disabled={Boolean(
                  item.instance &&
                    item.instance.status !== ConnectorStatus.ConnectionFailed &&
                    item.instance.status !== ConnectorStatus.Connected &&
                    item.instance.status !== ConnectorStatus.Enabling &&
                    item.instance.status !== ConnectorStatus.EnablementFailed,
                )}
                className="odin-flex odin-w-full odin-items-start odin-gap-4 odin-bg-white odin-p-4 odin-text-left hover:odin-bg-gray-50 disabled:odin-cursor-not-allowed disabled:odin-bg-gray-50"
                onClick={() => onSelect(item)}
              >
                <span className="odin-flex odin-h-10 odin-w-10 odin-flex-none odin-items-center odin-justify-center">
                  <img
                    src={connectorCatalog.getEntry(item.type).mark}
                    alt=""
                    className="odin-block odin-h-9 odin-w-9"
                  />
                </span>
                <span className="odin-min-w-0 odin-flex-1">
                  <span className="odin-flex odin-items-start odin-justify-between odin-gap-3">
                    <span className="odin-block odin-text-sm odin-font-medium odin-text-gray-900">
                      {item.displayName}
                    </span>
                    {instanceStatus && InstanceStatusIcon ? (
                      <span
                        aria-label={instanceStatus.label}
                        title={instanceStatus.label}
                        className={`odin-flex-none ${instanceStatus.className}`}
                      >
                        <InstanceStatusIcon
                          className="odin-h-5 odin-w-5"
                          aria-hidden="true"
                        />
                      </span>
                    ) : null}
                  </span>
                  <span className="odin-mt-2 odin-block odin-text-sm odin-leading-5 odin-text-gray-500">
                    {item.setupDescription}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
